/**
 * Intelligent Logo Color Extractor & Dynamic Theme Engine for CoachHub ERP
 * 
 * Features:
 * 1. Automatically extracts primary & accent colors from uploaded logos using Canvas pixel clustering.
 * 2. Injects CSS Custom Properties (--brand-primary, --brand-secondary, etc.) across the DOM.
 * 3. Applies the institute theme seamlessly to Student, Parent, Teacher, Staff, and Admin portals.
 * 4. Provides curated educational and institute color presets.
 */

import { resolveMediaUrl } from './mediaUtils';

export const THEME_PRESETS = [
  {
    id: 'indigo_horizon',
    name: 'Royal Indigo (Default)',
    primary: '#4f46e5',
    secondary: '#06b6d4',
    bgLight: '#eef2ff',
    badge: 'Popular',
  },
  {
    id: 'emerald_elite',
    name: 'Emerald Green (School & Foundation)',
    primary: '#059669',
    secondary: '#10b981',
    bgLight: '#ecfdf5',
    badge: 'Academic',
  },
  {
    id: 'sapphire_prestige',
    name: 'Sapphire Blue (College & University)',
    primary: '#1d4ed8',
    secondary: '#38bdf8',
    bgLight: '#eff6ff',
    badge: 'Classic',
  },
  {
    id: 'crimson_dynamic',
    name: 'Crimson Flame (Competitive Coaching)',
    primary: '#dc2626',
    secondary: '#f97316',
    bgLight: '#fef2f2',
    badge: 'Vibrant',
  },
  {
    id: 'royal_purple',
    name: 'Imperial Violet (Premium Academy)',
    primary: '#7c3aed',
    secondary: '#ec4899',
    bgLight: '#f5f3ff',
    badge: 'Modern',
  },
  {
    id: 'midnight_gold',
    name: 'Midnight & Gold (Excellence)',
    primary: '#0f172a',
    secondary: '#d97706',
    bgLight: '#f8fafc',
    badge: 'Executive',
  },
];

/**
 * Convert Hex to RGB { r, g, b }
 */
export function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return { r: 79, g: 70, b: 229 };
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = clean.split('').map(c => c + c).join('');
  }
  const num = parseInt(clean, 16);
  if (isNaN(num)) return { r: 79, g: 70, b: 229 };
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

/**
 * Convert RGB to Hex string
 */
export function rgbToHex(r, g, b) {
  const toHex = (c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Adjust Hex Color brightness / shade
 */
export function adjustBrightness(hex, percent) {
  const { r, g, b } = hexToRgb(hex);
  if (percent > 0) {
    // Soft tint toward white
    const factor = percent / 100;
    return rgbToHex(
      r + (255 - r) * factor,
      g + (255 - g) * factor,
      b + (255 - b) * factor
    );
  }
  // Darken
  const factor = 1 + percent / 100;
  return rgbToHex(
    Math.max(0, r * factor),
    Math.max(0, g * factor),
    Math.max(0, b * factor)
  );
}

/**
 * Convert Hex to HSL { h, s, l }
 */
export function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break;
      case gNorm: h = (bNorm - rNorm) / d + 2; break;
      case bNorm: h = (rNorm - gNorm) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360 * 10) / 10,
    s: Math.round(s * 100 * 10) / 10,
    l: Math.round(l * 100 * 10) / 10,
  };
}

/**
 * Extract Dominant Colors from Logo image file or DataURL
 * Returns { primary: string, secondary: string, palette: string[], confidence: number }
 */
export async function extractThemeFromLogo(source) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const maxDim = 80;
          let w = img.naturalWidth || img.width;
          let h = img.naturalHeight || img.height;
          const scale = Math.min(maxDim / w, maxDim / h, 1);
          w = Math.max(1, Math.round(w * scale));
          h = Math.max(1, Math.round(h * scale));

          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);

          const imgData = ctx.getImageData(0, 0, w, h).data;
          const colorBuckets = {};

          for (let i = 0; i < imgData.length; i += 4) {
            const r = imgData[i];
            const g = imgData[i + 1];
            const b = imgData[i + 2];
            const a = imgData[i + 3];

            // Ignore transparent or near-transparent pixels
            if (a < 128) continue;

            // Ignore near-white (backgrounds) and near-black pixels
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const delta = max - min;
            const lightness = (max + min) / 2;

            if (lightness > 245 || lightness < 15) continue;
            // Ignore low-saturation grays if we have colored pixels
            const isGray = delta < 18;

            // Quantize to 24-step bins
            const qr = Math.round(r / 24) * 24;
            const qg = Math.round(g / 24) * 24;
            const qb = Math.round(b / 24) * 24;
            const key = `${qr},${qg},${qb}`;

            // Score colorful pixels higher than grayscale
            const weight = isGray ? 0.3 : (1 + delta / 50);
            colorBuckets[key] = (colorBuckets[key] || 0) + weight;
          }

          // Sort buckets by frequency
          const sorted = Object.entries(colorBuckets).sort((a, b) => b[1] - a[1]);

          if (sorted.length === 0) {
            // Fallback if logo was purely monochrome or transparent
            resolve({
              primary: '#4f46e5',
              secondary: '#06b6d4',
              palette: ['#4f46e5', '#06b6d4', '#3b82f6'],
              isDefault: true,
            });
            return;
          }

          const palette = sorted.slice(0, 5).map(([key]) => {
            const [r, g, b] = key.split(',').map(Number);
            return rgbToHex(r, g, b);
          });

          const primary = palette[0] || '#4f46e5';
          let secondary = palette[1] || adjustBrightness(primary, 25);

          // If secondary is too close to primary, generate complementary hue
          if (palette.length > 1) {
            const rgb1 = hexToRgb(primary);
            const rgb2 = hexToRgb(secondary);
            const diff = Math.abs(rgb1.r - rgb2.r) + Math.abs(rgb1.g - rgb2.g) + Math.abs(rgb1.b - rgb2.b);
            if (diff < 60 && palette[2]) {
              secondary = palette[2];
            }
          }

          resolve({
            primary,
            secondary,
            palette,
            isDefault: false,
          });
        } catch (err) {
          console.warn('Color extraction canvas error:', err);
          resolve({ primary: '#4f46e5', secondary: '#06b6d4', palette: ['#4f46e5'], isDefault: true });
        }
      };

      img.onerror = () => {
        resolve({ primary: '#4f46e5', secondary: '#06b6d4', palette: ['#4f46e5'], isDefault: true });
      };

      if (typeof source === 'string') {
        img.src = source;
      } else if (source instanceof Blob || source instanceof File) {
        img.src = URL.createObjectURL(source);
      } else {
        resolve({ primary: '#4f46e5', secondary: '#06b6d4', palette: ['#4f46e5'], isDefault: true });
      }
    } catch {
      resolve({ primary: '#4f46e5', secondary: '#06b6d4', palette: ['#4f46e5'], isDefault: true });
    }
  });
}

/**
 * Injects dynamic CSS variables into document root for white-label styling across all portals
 */
export function applyTheme(brandingOrColors) {
  if (typeof document === 'undefined') return;

  const primary = brandingOrColors?.primary_color || brandingOrColors?.primary || brandingOrColors?.institute_primary_color || '#4f46e5';
  const secondary = brandingOrColors?.secondary_color || brandingOrColors?.secondary || brandingOrColors?.institute_secondary_color || '#06b6d4';

  const root = document.documentElement;
  const pRgb = hexToRgb(primary);
  const sRgb = hexToRgb(secondary);

  // Soft tints (toward white)
  const calcTint = (r, g, b, factor) => {
    const tr = Math.round(r + (255 - r) * factor);
    const tg = Math.round(g + (255 - g) * factor);
    const tb = Math.round(b + (255 - b) * factor);
    return { hex: rgbToHex(tr, tg, tb), rgb: `${tr}, ${tg}, ${tb}` };
  };

  // Dark shades
  const calcShade = (r, g, b, factor) => {
    const sr = Math.max(0, Math.round(r * factor));
    const sg = Math.max(0, Math.round(g * factor));
    const sb = Math.max(0, Math.round(b * factor));
    return { hex: rgbToHex(sr, sg, sb), rgb: `${sr}, ${sg}, ${sb}` };
  };

  const p50 = calcTint(pRgb.r, pRgb.g, pRgb.b, 0.94);
  const p100 = calcTint(pRgb.r, pRgb.g, pRgb.b, 0.88);
  const p200 = calcTint(pRgb.r, pRgb.g, pRgb.b, 0.75);
  const p300 = calcTint(pRgb.r, pRgb.g, pRgb.b, 0.55);
  const p400 = calcTint(pRgb.r, pRgb.g, pRgb.b, 0.25);
  const p700 = calcShade(pRgb.r, pRgb.g, pRgb.b, 0.88);
  const p800 = calcShade(pRgb.r, pRgb.g, pRgb.b, 0.75);
  const p900 = calcShade(pRgb.r, pRgb.g, pRgb.b, 0.62);
  const p950 = calcShade(pRgb.r, pRgb.g, pRgb.b, 0.40);

  const s50 = calcTint(sRgb.r, sRgb.g, sRgb.b, 0.94);
  const s100 = calcTint(sRgb.r, sRgb.g, sRgb.b, 0.88);
  const s200 = calcTint(sRgb.r, sRgb.g, sRgb.b, 0.75);
  const s300 = calcTint(sRgb.r, sRgb.g, sRgb.b, 0.55);
  const s400 = calcTint(sRgb.r, sRgb.g, sRgb.b, 0.25);
  const s700 = calcShade(sRgb.r, sRgb.g, sRgb.b, 0.88);
  const s800 = calcShade(sRgb.r, sRgb.g, sRgb.b, 0.75);
  const s900 = calcShade(sRgb.r, sRgb.g, sRgb.b, 0.62);

  // Set CSS Variables for Primary Brand
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-primary-rgb', `${pRgb.r}, ${pRgb.g}, ${pRgb.b}`);
  root.style.setProperty('--brand-primary-hover', p700.hex);
  root.style.setProperty('--brand-primary-hover-rgb', p700.rgb);
  root.style.setProperty('--brand-primary-light', p50.hex);
  root.style.setProperty('--brand-primary-light-rgb', p50.rgb);
  root.style.setProperty('--brand-primary-border', p200.hex);
  root.style.setProperty('--brand-primary-border-rgb', p200.rgb);

  root.style.setProperty('--brand-primary-50', p50.hex);
  root.style.setProperty('--brand-primary-50-rgb', p50.rgb);
  root.style.setProperty('--brand-primary-100', p100.hex);
  root.style.setProperty('--brand-primary-100-rgb', p100.rgb);
  root.style.setProperty('--brand-primary-200', p200.hex);
  root.style.setProperty('--brand-primary-200-rgb', p200.rgb);
  root.style.setProperty('--brand-primary-300', p300.hex);
  root.style.setProperty('--brand-primary-300-rgb', p300.rgb);
  root.style.setProperty('--brand-primary-400', p400.hex);
  root.style.setProperty('--brand-primary-400-rgb', p400.rgb);
  root.style.setProperty('--brand-primary-700', p700.hex);
  root.style.setProperty('--brand-primary-700-rgb', p700.rgb);
  root.style.setProperty('--brand-primary-800', p800.hex);
  root.style.setProperty('--brand-primary-800-rgb', p800.rgb);
  root.style.setProperty('--brand-primary-900', p900.hex);
  root.style.setProperty('--brand-primary-900-rgb', p900.rgb);
  root.style.setProperty('--brand-primary-950', p950.hex);
  root.style.setProperty('--brand-primary-950-rgb', p950.rgb);

  // Set CSS Variables for Secondary Accent
  root.style.setProperty('--brand-secondary', secondary);
  root.style.setProperty('--brand-secondary-rgb', `${sRgb.r}, ${sRgb.g}, ${sRgb.b}`);
  root.style.setProperty('--brand-secondary-hover', s700.hex);
  root.style.setProperty('--brand-secondary-hover-rgb', s700.rgb);
  root.style.setProperty('--brand-secondary-light', s50.hex);
  root.style.setProperty('--brand-secondary-light-rgb', s50.rgb);
  root.style.setProperty('--brand-secondary-border', s200.hex);
  root.style.setProperty('--brand-secondary-border-rgb', s200.rgb);

  root.style.setProperty('--brand-secondary-50', s50.hex);
  root.style.setProperty('--brand-secondary-50-rgb', s50.rgb);
  root.style.setProperty('--brand-secondary-100', s100.hex);
  root.style.setProperty('--brand-secondary-100-rgb', s100.rgb);
  root.style.setProperty('--brand-secondary-200', s200.hex);
  root.style.setProperty('--brand-secondary-200-rgb', s200.rgb);
  root.style.setProperty('--brand-secondary-300', s300.hex);
  root.style.setProperty('--brand-secondary-300-rgb', s300.rgb);
  root.style.setProperty('--brand-secondary-400', s400.hex);
  root.style.setProperty('--brand-secondary-400-rgb', s400.rgb);
  root.style.setProperty('--brand-secondary-700', s700.hex);
  root.style.setProperty('--brand-secondary-700-rgb', s700.rgb);
  root.style.setProperty('--brand-secondary-800', s800.hex);
  root.style.setProperty('--brand-secondary-800-rgb', s800.rgb);
  root.style.setProperty('--brand-secondary-900', s900.hex);
  root.style.setProperty('--brand-secondary-900-rgb', s900.rgb);

  // Set HSL for Shadcn / Radix component variables
  const pHsl = hexToHsl(primary);
  const sHsl = hexToHsl(secondary);
  root.style.setProperty('--primary', `${pHsl.h} ${pHsl.s}% ${pHsl.l}%`);
  root.style.setProperty('--ring', `${pHsl.h} ${pHsl.s}% ${pHsl.l}%`);
  root.style.setProperty('--chart-1', `${pHsl.h} ${pHsl.s}% ${pHsl.l}%`);
  root.style.setProperty('--chart-2', `${sHsl.h} ${sHsl.s}% ${sHsl.l}%`);

  // Update mobile browser status bar / theme color meta tag
  try {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', primary);
  } catch {}

  // Sync to localStorage
  try {
    const cached = JSON.parse(localStorage.getItem('institute_branding') || '{}');
    const updated = {
      ...cached,
      primary_color: primary,
      secondary_color: secondary,
    };
    localStorage.setItem('institute_branding', JSON.stringify(updated));
  } catch {}

  // Broadcast theme change event
  try {
    window.dispatchEvent(new CustomEvent('institute-theme-applied', { detail: { primary, secondary } }));
  } catch {}
}

/**
 * Initializes theme from localStorage on initial page load
 */
export function initThemeFromStorage() {
  if (typeof window === 'undefined') return;
  try {
    const cached = localStorage.getItem('institute_branding');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.primary_color || parsed.secondary_color) {
        applyTheme(parsed);
        return;
      }
    }
  } catch {}
  // No institute branding: make sure the public website palette doesn't leak into dashboards
  resetThemeToDefaults();
}

/**
 * Default CoachHub brand colors — used on public/landing pages
 * and when no institute branding is active.
 */
const DEFAULT_BRAND = {
  primary: '#4f46e5',
  secondary: '#06b6d4',
};

/**
 * Resets all dynamic CSS brand variables back to CoachHub defaults.
 * Call this when navigating away from the dashboard to public pages,
 * or on logout, so the landing page / website is never affected by
 * per-institute branding.
 */
export function resetThemeToDefaults() {
  if (typeof document === 'undefined') return;
  // Public website routes always carry the website palette, even after logout/session reset
  if (isPublicRoute()) {
    applyPublicSiteTheme();
    return;
  }
  applyTheme({
    primary_color: DEFAULT_BRAND.primary,
    secondary_color: DEFAULT_BRAND.secondary,
  });

  // Reset the mobile status-bar meta tag
  try {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', DEFAULT_BRAND.primary);
  } catch {}
}

/**
 * Public website palette (deep navy + logo gold). Applied on public routes so
 * every marketing page — including ones styled with indigo/cyan utilities —
 * renders in the mycoachhub.in website theme.
 */
const PUBLIC_SITE_BRAND = {
  primary: '#163A86',
  secondary: '#E3A21A',
  themeColor: '#0B1F4D',
};

export function applyPublicSiteTheme() {
  if (typeof document === 'undefined') return;
  applyTheme({
    primary_color: PUBLIC_SITE_BRAND.primary,
    secondary_color: PUBLIC_SITE_BRAND.secondary,
  });
  try {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', PUBLIC_SITE_BRAND.themeColor);
  } catch {}
}

/**
 * Resets the browser favicon back to the default CoachHub icon.
 * Prevents a previously-logged-in institute's favicon from showing
 * on public/landing pages.
 */
export function resetFaviconToDefault() {
  if (typeof document === 'undefined') return;
  try {
    let link = document.querySelector("link[rel~='icon']");
    if (link) {
      // Restore the original /favicon.ico shipped with the app
      link.href = '/favicon.ico';
    }
  } catch {}
}

/**
 * Returns true if the given or current path is a public/website route
 * (Landing page, features, institutes, public profiles, about, etc.)
 * as opposed to an authenticated ERP dashboard/portal route.
 */
export function isPublicRoute(pathname) {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const isDashboard =
    path.startsWith('/institute-panel') ||
    path.startsWith('/teacher') ||
    path.startsWith('/student') ||
    path.startsWith('/parent') ||
    path.startsWith('/admin') ||
    path === '/dashboard';

  return !isDashboard;
}

/**
 * Updates PWA Web App Manifest, Apple touch icon, and mobile app title
 * dynamically so when installed on laptop or mobile (Android/iOS), the
 * installed app displays the logged-in institute's icon, name, and theme color.
 */
// Cached branding (e.g. saved by the Settings page) may lack the institute id; the signed-in
// user always has it.
function resolveInstituteId(branding) {
  const fromBranding = branding?.institute_id || branding?.id;
  if (fromBranding) return fromBranding;
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.institute_id || null;
  } catch {
    return null;
  }
}

export function updatePwaManifest(branding) {
  if (typeof document === 'undefined') return;
  try {
    const instName = branding?.name || 'CoachHub';
    const primaryColor = branding?.primary_color || DEFAULT_BRAND.primary;
    const iconUrl = branding?.favicon || branding?.logo;
    const resolvedIcon = iconUrl ? resolveMediaUrl(iconUrl) : null;

    // 1. Update Apple touch icon (Mobile Safari / iOS home screen)
    let appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleIcon) {
      appleIcon = document.createElement('link');
      appleIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleIcon);
    }
    // iOS uses this for the home-screen icon: prefer the square icon rendered by the backend.
    const brandedInstituteId = resolveInstituteId(branding);
    appleIcon.href = brandedInstituteId && process.env.REACT_APP_BACKEND_URL
      ? `${process.env.REACT_APP_BACKEND_URL}/api/institute/${brandedInstituteId}/app-icon/192.png`
      : (resolvedIcon || '/icons/icon-192x192.png');

    // 2. Update Apple App Title
    let appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = instName;

    // 3. Update theme-color meta tag
    let metaTheme = document.querySelector("meta[name='theme-color']");
    if (metaTheme) {
      metaTheme.setAttribute('content', primaryColor);
    }

    // 4. Update Tab Favicon
    let favLink = document.querySelector("link[rel~='icon']");
    if (favLink && resolvedIcon) {
      favLink.href = resolvedIcon;
    }

    // 5. Update Web App Manifest link
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }

    // Point to the backend's institute manifest. Never use a data: manifest: Android can only
    // install a real app from a manifest URL; a data: one gives a shortcut with a Chrome badge.
    const instituteId = resolveInstituteId(branding);
    manifestLink.href = instituteId && process.env.REACT_APP_BACKEND_URL
      ? `${process.env.REACT_APP_BACKEND_URL}/api/institute/${instituteId}/manifest.json`
      : '/manifest.json';
  } catch (err) {
    console.warn('Could not update PWA manifest:', err);
  }
}

/**
 * Resets PWA manifest, Apple touch icon, and app title back to CoachHub defaults.
 */
export function resetPwaManifest() {
  if (typeof document === 'undefined') return;
  try {
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (manifestLink) {
      manifestLink.setAttribute('href', '/manifest.json');
    }
    let appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (appleIcon) {
      appleIcon.href = '/icons/icon-192x192.png';
    }
    let appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appleTitle) {
      appleTitle.content = 'CoachHub';
    }
    let metaTheme = document.querySelector("meta[name='theme-color']");
    if (metaTheme) {
      metaTheme.setAttribute('content', DEFAULT_BRAND.primary);
    }
    resetFaviconToDefault();
  } catch {}
}

